import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Leaf, ArrowRight, ArrowLeft, Check, Euro, Users, Building2,
  Sparkles, Bot, Shield, Clock, Zap, Package, Tractor, Map,
  Calculator, Calendar, Droplets, Cloud, BarChart3, Grape,
  Beef, Rabbit, Carrot, Apple, Wheat, Flower2, Factory,
  Eye, EyeOff, CreditCard, Gift, Loader2, CheckCircle2, Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// TARIFICATION BUREAU AGRICOLE
// Prix de base : 149€/mois (1 utilisateur, 1 workspace, modules de base)
// +29€/collaborateur supplémentaire
// +49€/workspace supplémentaire
// Essai : 14 jours gratuits puis blocage

const PRIX_BASE = 149; // €/mois
const PRIX_COLLABORATEUR = 29; // €/mois par collaborateur supplémentaire
const PRIX_WORKSPACE = 49; // €/mois par workspace supplémentaire
const DUREE_ESSAI_JOURS = 14; // jours d'essai gratuit

// Configuration des modules disponibles
const MODULES_CONFIG = {
  parcellaire: { nom: 'Parcellaire', icon: Map, prix: 0, desc: 'Carte interactive, îlots PAC, assolement', inclus: true },
  interventions: { nom: 'Interventions', icon: Calendar, prix: 0, desc: 'Planning, suivi temps, coûts', inclus: true },
  stocks: { nom: 'Stocks', icon: Package, prix: 0, desc: 'Inventaire, alertes, traçabilité', inclus: true },
  crm: { nom: 'CRM', icon: Users, prix: 19, desc: 'Clients, prospects, pipeline' },
  comptabilite: { nom: 'Comptabilité', icon: Calculator, prix: 29, desc: 'Factures, dépenses, trésorerie' },
  registre_phyto: { nom: 'Registre Phyto', icon: Droplets, prix: 19, desc: 'Conforme, base EPHY intégrée' },
  materiel: { nom: 'Matériel', icon: Tractor, prix: 19, desc: 'Parc machines, entretien, coûts/ha' },
  meteo: { nom: 'Météo', icon: Cloud, prix: 9, desc: 'Prévisions, alertes gel/pluie' },
  bilans: { nom: 'Bilans', icon: BarChart3, prix: 19, desc: 'IFT, carbone, certifications' },
  assistants_ia: { nom: 'Assistants IA', icon: Bot, prix: 39, desc: '8 agents métiers intelligents' },
};

// Packs métiers
const PACKS_METIERS = {
  viticulture: { nom: 'Viticulture', icon: Grape, prix: 29, couleur: '#7c3aed', modules: ['registre_phyto', 'bilans'] },
  elevage: { nom: 'Élevage', icon: Beef, prix: 29, couleur: '#ea580c', modules: ['stocks'] },
  maraichage: { nom: 'Maraîchage', icon: Carrot, prix: 19, couleur: '#16a34a', modules: ['stocks', 'crm'] },
  arboriculture: { nom: 'Arboriculture', icon: Apple, prix: 29, couleur: '#dc2626', modules: ['registre_phyto', 'bilans'] },
  grandes_cultures: { nom: 'Grandes cultures', icon: Wheat, prix: 19, couleur: '#ca8a04', modules: ['registre_phyto'] },
  ppam: { nom: 'PPAM / Aromatiques', icon: Flower2, prix: 29, couleur: '#059669', modules: ['stocks', 'bilans'] },
  eta: { nom: 'ETA / Prestation', icon: Tractor, prix: 39, couleur: '#2563eb', modules: ['materiel', 'comptabilite'] },
  transformation: { nom: 'Transformation', icon: Factory, prix: 29, couleur: '#9333ea', modules: ['stocks', 'bilans'] },
};

const InscriptionPubliquePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    // Step 1: Activité
    activites: [],
    surface_ha: 50,
    
    // Step 2: Modules
    modules: ['parcellaire', 'interventions', 'stocks'],
    
    // Step 3: Dimensionnement
    nb_collaborateurs: 1,
    nb_workspaces: 1,
    
    // Step 4: Compte
    nom_entreprise: '',
    prenom: '',
    nom: '',
    email: '',
    password: '',
    telephone: '',
    code_parrainage: searchParams.get('parrain') || '',
    
    // Paiement
    type_paiement: 'mensuel', // mensuel, annuel
    accepte_cgv: false,
  });
  
  // Prix calculé
  const [prix, setPrix] = useState({ mensuel: 0, annuel: 0, essai_jours: DUREE_ESSAI_JOURS });
  const [parrainageValide, setParrainageValide] = useState(false);
  const [suggestionsIA, setSuggestionsIA] = useState(null);
  
  // Calculer le prix à chaque modification
  useEffect(() => {
    calculerPrix();
  }, [formData.activites, formData.modules, formData.nb_collaborateurs, formData.nb_workspaces, formData.code_parrainage]);

  const calculerPrix = async () => {
    // Prix de base : 149€/mois
    let prixBase = PRIX_BASE;
    
    // Prix des modules sélectionnés
    const prixModules = formData.modules.reduce((acc, modId) => {
      return acc + (MODULES_CONFIG[modId]?.prix || 0);
    }, 0);
    
    // Prix des packs métiers
    const prixPacks = formData.activites.reduce((acc, packId) => {
      return acc + (PACKS_METIERS[packId]?.prix || 0);
    }, 0);
    
    // Prix par collaborateur supplémentaire (au-delà de 1) : 29€/collab
    const prixCollabs = Math.max(0, formData.nb_collaborateurs - 1) * PRIX_COLLABORATEUR;
    
    // Prix par workspace supplémentaire (au-delà de 1) : 49€/workspace
    const prixWorkspaces = Math.max(0, formData.nb_workspaces - 1) * PRIX_WORKSPACE;
    
    let mensuel = prixBase + prixModules + prixPacks + prixCollabs + prixWorkspaces;
    
    // Réduction parrainage
    if (formData.code_parrainage) {
      try {
        const res = await fetch(`${API_URL}/api/tunnel-vente/verifier-parrainage/${formData.code_parrainage}`);
        if (res.ok) {
          const data = await res.json();
          setParrainageValide(data.valide);
          if (data.valide) {
            mensuel = mensuel * 0.9; // -10%
          }
        }
      } catch (e) {
        // Ignore
      }
    }
    
    // Annuel = -15%
    const annuel = Math.round(mensuel * 12 * 0.85);
    
    setPrix({
      mensuel: Math.round(mensuel),
      annuel,
      essai_jours: DUREE_ESSAI_JOURS
    });
  };

  // Obtenir suggestions IA
  const obtenirSuggestions = async () => {
    if (formData.activites.length === 0) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tunnel-vente/suggestions-ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activites: formData.activites,
          surface_ha: formData.surface_ha,
          nb_collaborateurs: formData.nb_collaborateurs
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSuggestionsIA(data);
        
        // Appliquer les modules suggérés automatiquement
        if (data.modules_suggeres) {
          setFormData(f => ({
            ...f,
            modules: [...new Set([...f.modules, ...data.modules_suggeres])]
          }));
        }
      }
    } catch (e) {
      // Suggestions manuelles en fallback
      const modulesAuto = formData.activites.flatMap(a => PACKS_METIERS[a]?.modules || []);
      setFormData(f => ({
        ...f,
        modules: [...new Set([...f.modules, ...modulesAuto])]
      }));
    }
  };

  // Toggle activité
  const toggleActivite = (packId) => {
    setFormData(f => ({
      ...f,
      activites: f.activites.includes(packId)
        ? f.activites.filter(a => a !== packId)
        : [...f.activites, packId]
    }));
  };

  // Toggle module
  const toggleModule = (modId) => {
    if (MODULES_CONFIG[modId]?.inclus) return; // Modules de base non désactivables
    
    setFormData(f => ({
      ...f,
      modules: f.modules.includes(modId)
        ? f.modules.filter(m => m !== modId)
        : [...f.modules, modId]
    }));
  };

  // Soumettre l'inscription
  const handleSubmit = async () => {
    if (!formData.accepte_cgv) {
      toast.error('Veuillez accepter les conditions générales');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/tunnel-vente/inscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          prix_mensuel: prix.mensuel,
          prix_annuel: prix.annuel,
          essai_gratuit: true,
          duree_essai_jours: 30
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        toast.success('Inscription réussie !', {
          description: `Votre essai gratuit de ${DUREE_ESSAI_JOURS} jours commence maintenant.`
        });
        
        // Rediriger vers le paiement ou connexion
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          navigate('/login');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Navigation entre steps
  const nextStep = () => {
    if (step === 1 && formData.activites.length === 0) {
      toast.error('Sélectionnez au moins une activité');
      return;
    }
    if (step === 1) {
      obtenirSuggestions();
    }
    setStep(s => Math.min(s + 1, 4));
  };
  
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-emerald-700 text-white py-6">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Leaf className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Bureau Agricole</h1>
                <p className="text-sm text-green-100">Créez votre compte</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-400 text-yellow-900">
                <Gift className="w-3 h-3 mr-1" />
                {DUREE_ESSAI_JOURS} jours gratuits
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Activité' },
              { num: 2, label: 'Modules' },
              { num: 3, label: 'Équipe' },
              { num: 4, label: 'Compte' },
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    step > s.num ? 'bg-green-600 text-white' :
                    step === s.num ? 'bg-green-100 text-green-600 border-2 border-green-600' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="hidden sm:inline font-medium">{s.label}</span>
                </div>
                {i < 3 && (
                  <div className={`w-8 sm:w-16 h-1 mx-2 rounded ${step > s.num ? 'bg-green-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulaire */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* STEP 1: Activité */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-600" />
                    Quelle est votre activité ?
                  </CardTitle>
                  <CardDescription>
                    Sélectionnez vos métiers - nous activerons automatiquement les modules adaptés
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(PACKS_METIERS).map(([packId, pack]) => {
                      const Icon = pack.icon;
                      const isSelected = formData.activites.includes(packId);
                      
                      return (
                        <div
                          key={packId}
                          onClick={() => toggleActivite(packId)}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                            isSelected 
                              ? 'border-green-500 bg-green-50 shadow-md' 
                              : 'border-gray-200 hover:border-green-200 hover:bg-green-50/30'
                          }`}
                          data-testid={`pack-${packId}`}
                        >
                          <div 
                            className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2"
                            style={{ backgroundColor: pack.couleur + '20' }}
                          >
                            <Icon className="w-6 h-6" style={{ color: pack.couleur }} />
                          </div>
                          <h4 className="font-semibold text-sm">{pack.nom}</h4>
                          {isSelected && (
                            <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mt-2" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Surface exploitée</Label>
                      <span className="font-bold text-green-600">{formData.surface_ha} ha</span>
                    </div>
                    <Slider
                      value={[formData.surface_ha]}
                      onValueChange={([v]) => setFormData(f => ({ ...f, surface_ha: v }))}
                      min={0}
                      max={500}
                      step={10}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 2: Modules */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-green-600" />
                    Personnalisez vos modules
                  </CardTitle>
                  <CardDescription>
                    Les modules de base sont inclus. Ajoutez ceux dont vous avez besoin.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {suggestionsIA && (
                    <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-800">Suggestion IA</span>
                      </div>
                      <p className="text-sm text-purple-700">{suggestionsIA.explication}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(MODULES_CONFIG).map(([modId, mod]) => {
                      const Icon = mod.icon;
                      const isSelected = formData.modules.includes(modId);
                      const isInclus = mod.inclus;
                      
                      return (
                        <div
                          key={modId}
                          onClick={() => toggleModule(modId)}
                          className={`p-3 border rounded-lg transition-all ${
                            isInclus ? 'bg-green-50 border-green-200 cursor-default' :
                            isSelected ? 'border-green-500 bg-green-50 cursor-pointer' :
                            'border-gray-200 hover:border-green-200 cursor-pointer'
                          }`}
                          data-testid={`module-${modId}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${isSelected || isInclus ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <Icon className={`w-4 h-4 ${isSelected || isInclus ? 'text-green-600' : 'text-gray-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{mod.nom}</span>
                                {isInclus ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Inclus</Badge>
                                ) : isSelected ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : null}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{mod.desc}</p>
                              {!isInclus && mod.prix > 0 && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  +{mod.prix}€/mois
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 3: Équipe */}
            {step === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    Dimensionnez votre équipe
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <Label>Nombre de collaborateurs</Label>
                      <p className="text-sm text-muted-foreground">
                        Combien de personnes utiliseront le logiciel ?
                      </p>
                      <div className="flex items-center gap-4">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setFormData(f => ({ ...f, nb_collaborateurs: Math.max(1, f.nb_collaborateurs - 1) }))}
                        >
                          -
                        </Button>
                        <span className="text-3xl font-bold w-16 text-center">{formData.nb_collaborateurs}</span>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setFormData(f => ({ ...f, nb_collaborateurs: f.nb_collaborateurs + 1 }))}
                        >
                          +
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        1 inclus, puis +{PRIX_COLLABORATEUR}€/mois par collaborateur
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <Label>Nombre de sociétés/workspaces</Label>
                      <p className="text-sm text-muted-foreground">
                        Si vous gérez plusieurs structures (EARL, SCEA...)
                      </p>
                      <div className="flex items-center gap-4">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setFormData(f => ({ ...f, nb_workspaces: Math.max(1, f.nb_workspaces - 1) }))}
                        >
                          -
                        </Button>
                        <span className="text-3xl font-bold w-16 text-center">{formData.nb_workspaces}</span>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setFormData(f => ({ ...f, nb_workspaces: f.nb_workspaces + 1 }))}
                        >
                          +
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        1 inclus, puis +{PRIX_WORKSPACE}€/mois par workspace
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 4: Compte */}
            {step === 4 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-green-600" />
                    Créez votre compte
                  </CardTitle>
                  <CardDescription>
                    Vos {DUREE_ESSAI_JOURS} jours d'essai gratuit commencent immédiatement
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom de l'entreprise *</Label>
                    <Input
                      value={formData.nom_entreprise}
                      onChange={(e) => setFormData(f => ({ ...f, nom_entreprise: e.target.value }))}
                      placeholder="EARL Les Vignes du Soleil"
                      data-testid="input-entreprise"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom *</Label>
                      <Input
                        value={formData.prenom}
                        onChange={(e) => setFormData(f => ({ ...f, prenom: e.target.value }))}
                        placeholder="Jean"
                        data-testid="input-prenom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={formData.nom}
                        onChange={(e) => setFormData(f => ({ ...f, nom: e.target.value }))}
                        placeholder="Dupont"
                        data-testid="input-nom"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                      placeholder="jean.dupont@exploitation.fr"
                      data-testid="input-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mot de passe *</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                        placeholder="Minimum 8 caractères"
                        data-testid="input-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData(f => ({ ...f, telephone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Code parrainage (optionnel)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.code_parrainage}
                          onChange={(e) => setFormData(f => ({ ...f, code_parrainage: e.target.value.toUpperCase() }))}
                          placeholder="DUPONT-A7X"
                        />
                        {parrainageValide && (
                          <Badge className="bg-green-100 text-green-800 whitespace-nowrap">
                            <Check className="w-3 h-3 mr-1" />
                            -10%
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Type de facturation</Label>
                      <Select 
                        value={formData.type_paiement} 
                        onValueChange={(v) => setFormData(f => ({ ...f, type_paiement: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensuel">Mensuel - {prix.mensuel}€/mois</SelectItem>
                          <SelectItem value="annuel">Annuel - {Math.round(prix.annuel / 12)}€/mois (-15%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Checkbox 
                        id="cgv" 
                        checked={formData.accepte_cgv}
                        onCheckedChange={(c) => setFormData(f => ({ ...f, accepte_cgv: c }))}
                      />
                      <Label htmlFor="cgv" className="text-sm leading-tight">
                        J'accepte les{' '}
                        <a href="#" className="text-green-600 hover:underline">conditions générales de vente</a>
                        {' '}et la{' '}
                        <a href="#" className="text-green-600 hover:underline">politique de confidentialité</a>
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex justify-between">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => navigate('/bureau-agricole')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Accueil
                </Button>
              )}
              
              {step < 4 ? (
                <Button onClick={nextStep} className="bg-green-600 hover:bg-green-700">
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading || !formData.email || !formData.password || !formData.nom_entreprise || !formData.accepte_cgv}
                  data-testid="btn-inscription"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Commencer mon essai gratuit
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar: Récapitulatif prix */}
          <div className="space-y-4">
            <Card className="sticky top-24 border-2 border-green-200 bg-gradient-to-b from-green-50 to-white">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Euro className="w-5 h-5 text-green-600" />
                  Votre abonnement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Essai gratuit */}
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <Gift className="w-4 h-4" />
                    <span className="font-semibold">{DUREE_ESSAI_JOURS} jours gratuits</span>
                  </div>
                  <p className="text-xs text-yellow-700 mt-1">
                    Sans engagement, annulez à tout moment
                  </p>
                </div>
                
                <Separator />
                
                {/* Détails prix */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Abonnement de base</span>
                    <span>{PRIX_BASE}€</span>
                  </div>
                  
                  {formData.activites.length > 0 && (
                    <div className="flex justify-between">
                      <span>Packs métiers ({formData.activites.length})</span>
                      <span>+{formData.activites.reduce((acc, a) => acc + (PACKS_METIERS[a]?.prix || 0), 0)}€</span>
                    </div>
                  )}
                  
                  {formData.modules.filter(m => !MODULES_CONFIG[m]?.inclus && MODULES_CONFIG[m]?.prix > 0).length > 0 && (
                    <div className="flex justify-between">
                      <span>Modules supplémentaires</span>
                      <span>+{formData.modules.filter(m => !MODULES_CONFIG[m]?.inclus).reduce((acc, m) => acc + (MODULES_CONFIG[m]?.prix || 0), 0)}€</span>
                    </div>
                  )}
                  
                  {formData.nb_collaborateurs > 1 && (
                    <div className="flex justify-between">
                      <span>Collaborateurs (+{formData.nb_collaborateurs - 1})</span>
                      <span>+{(formData.nb_collaborateurs - 1) * PRIX_COLLABORATEUR}€</span>
                    </div>
                  )}
                  
                  {formData.nb_workspaces > 1 && (
                    <div className="flex justify-between">
                      <span>Workspaces (+{formData.nb_workspaces - 1})</span>
                      <span>+{(formData.nb_workspaces - 1) * PRIX_WORKSPACE}€</span>
                    </div>
                  )}
                  
                  {parrainageValide && (
                    <div className="flex justify-between text-green-600">
                      <span>Réduction parrainage</span>
                      <span>-10%</span>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                {/* Total */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total mensuel</span>
                    <span className="text-2xl font-bold text-green-600">{prix.mensuel}€</span>
                  </div>
                  
                  {formData.type_paiement === 'annuel' && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Total annuel</span>
                      <span className="font-semibold">{prix.annuel}€ (-15%)</span>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                {/* Garanties */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="w-3 h-3" />
                    <span>Paiement sécurisé</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>Annulation sans frais</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-3 h-3" />
                    <span>Support inclus</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InscriptionPubliquePage;
